import { computed, ref, watch } from 'vue';
import { useLs } from '@/setup/vueLs';
import type { UiTableNewFieldBase } from '@/types/table';

interface HeaderDragendColumn {
  property?: string;
  columnKey?: string;
  width: number;
  minWidth: number;
}

interface TableColumnWidthsParams<T extends UiTableNewFieldBase> {
  lsKey: string;
  fields: T[];
}

interface StoredColumnWidth {
  name: string;
  width?: string | number;
}

function isStoredColumnWidth(item: unknown): item is StoredColumnWidth {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const entry = item as StoredColumnWidth;
  return !!entry.name && !!entry.width;
}

function normalizeStoredWidths(
  raw: unknown,
): Record<string, string | number> {
  if (Array.isArray(raw)) {
    return Object.fromEntries(
      raw
        .filter(isStoredColumnWidth)
        .map((item) => [item.name, item.width as string | number]),
    );
  }
  if (raw && typeof raw === 'object') {
    return { ...(raw as Record<string, string | number>) };
  }
  return {};
}

export function useTableColumnWidths<T extends UiTableNewFieldBase>(
  params: TableColumnWidthsParams<T>,
) {
  const ls = useLs();
  const { lsKey, fields: baseFields } = params;

  const stored = ref<Record<string, string | number>>(
    normalizeStoredWidths(ls.get(lsKey)),
  );

  watch(stored, (value) => {
    ls.set(lsKey, value);
  }, { deep: true });

  const fields = computed(() => {
    return baseFields.map((field) => {
      const storedWidth = stored.value[field.name];
      if (storedWidth == null) {
        return field;
      }
      return {
        ...field,
        width: storedWidth,
      };
    });
  });

  const headerDragend = (
    newWidth: number,
    _delta: number,
    column: HeaderDragendColumn,
  ) => {
    const name = column.property || column.columnKey;
    if (!name || !Number.isFinite(newWidth)) {
      return;
    }

    const field = baseFields.find((item) => item.name === name);

    if (!field || field.resizable === false) {
      return;
    }

    const min = Number(column.minWidth) || 0;
    stored.value[name] = Math.max(newWidth, min);
  };

  return {
    fields,
    headerDragend,
  };
}
